import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getTagPerformance,
  getTagCoOccurrence,
  getTagPerformanceTimeseries,
} from '../store/analytics/thunks';
import { getTradesPage } from '../store/trades/thunks';
import { useNavigate } from 'react-router-dom';
import { Chart } from 'react-google-charts';
import {
  Box,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Center,
  VStack,
  HStack,
  Badge,
  useColorMode,
} from '@chakra-ui/react';
import getSymbolFromCurrency from 'currency-symbol-map';
import AnalyticsSkeleton from './AnalyticsSkeleton';
import FilterSelect from './common/FilterSelect';
import { formatPeriodLabel } from '../utils/period';

import '../styles/profile.css';

export default function TagAnalytics({ user }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const tagPerformance = useSelector((state) => state.analytics.tagPerformance);
  const tagCoOccurrence = useSelector((state) => state.analytics.tagCoOccurrence) || [];
  const tagTimeseries = useSelector((state) => state.analytics.tagTimeseries) || {};
  const loading = useSelector((state) => state.ui.loading);
  const preferences = useSelector((state) => state.user.preferences);

  const currencySymbol = getSymbolFromCurrency(preferences?.preferred_currency || 'USD') || '$';
  const [selectedTagId, setSelectedTagId] = useState("");

  useEffect(() => {
    dispatch(getTagPerformance());
    dispatch(getTagCoOccurrence());
  }, [dispatch]);

  useEffect(() => {
    if (selectedTagId) {
      dispatch(getTagPerformanceTimeseries({ tag_id: selectedTagId, period: 'week' }));
    }
  }, [dispatch, selectedTagId]);

  const handleTagClick = async (tagId) => {
    const filters = {
      page: 1,
      numrows: 100,
      tag_id: tagId,
    };
    window.localStorage.setItem('SummaryFilters', JSON.stringify(filters));
    await dispatch(getTradesPage({ filters }));
    navigate('/summary');
  };

  const barChartData = [
    ['Tag', 'Total P&L', { role: 'style' }],
    ...tagPerformance.map((t) => [
      t.name,
      t.total_pnl,
      t.total_pnl >= 0 ? '#48BB78' : '#FC8181',
    ]),
  ];

  const barChartOptions = {
    title: 'P&L by Tag',
    chartArea: { width: '70%', height: '80%' },
    height: '100%',
    width: '100%',
    backgroundColor: colorMode === 'light' ? '#FDFDFD' : '#1A202C',
    titleTextStyle: {
      color: colorMode === 'light' ? '#636363' : '#E2E8F0',
      fontName: 'Open Sans',
      bold: true,
      fontSize: 16,
    },
    hAxis: {
      title: `P&L (${currencySymbol})`,
      titleTextStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
      textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
    },
    vAxis: {
      textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
    },
    legend: 'none',
  };

  if (loading && tagPerformance.length === 0) {
    return (
      <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <VStack spacing={4} p={4} align="stretch" w="full">
            <AnalyticsSkeleton heatmapColumns={{ base: 2, sm: 3, md: 4, lg: 6 }} heatmapCount={8} />
          </VStack>
        </Box>
      </Flex>
    );
  }

  if (tagPerformance.length === 0) {
    return (
      <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <Center h="60vh">
            <Text color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
              No tag data yet. Create tags and assign them to trades to see tag analytics.
            </Text>
          </Center>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
      <Box flex="auto" overflowX="auto" overflowY="auto">
        <VStack spacing={4} p={4} align="stretch" w="full">
          <Heading className="page-heading" fontSize="md" color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
            Tag Analytics
          </Heading>

          {/* Heatmap */}
          <Box>
            <Heading size="sm" mb={3} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
              Performance Heatmap
            </Heading>
            <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={3}>
              {tagPerformance.map((t) => {
                const isPositive = t.total_pnl >= 0;
                return (
                  <Box
                    key={t.tag_id}
                    p={3}
                    rounded="md"
                    cursor="pointer"
                    bg={isPositive ? 'green.400' : 'red.400'}
                    color="white"
                    _hover={{ opacity: 0.85, transform: 'scale(1.03)', transition: '0.15s' }}
                    onClick={() => handleTagClick(t.tag_id)}
                    textAlign="center"
                  >
                    <Badge bg={t.color || '#3182CE'} color="white" fontSize="xs" px={2} py={0.5} rounded="md" mb={1}>
                      {t.name}
                    </Badge>
                    <Text fontSize="xs">
                      {isPositive ? '+' : ''}{currencySymbol}{t.total_pnl.toFixed(2)}
                    </Text>
                    <Text fontSize="xs" opacity={0.9}>
                      {t.win_rate !== null ? `${t.win_rate}% WR` : 'No closed trades'}
                    </Text>
                    <Text fontSize="xs" opacity={0.85}>
                      {t.num_trades} trade{t.num_trades !== 1 ? 's' : ''}
                    </Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>

          {/* Bar Chart */}
          <Box
            bg={colorMode === 'light' ? 'white' : 'gray.700'}
            rounded="lg"
            boxShadow="sm"
            p={4}
            h="400px"
          >
            <Chart
              chartType="BarChart"
              width="100%"
              height="100%"
              data={barChartData}
              options={barChartOptions}
            />
          </Box>

          {/* Table */}
          <Box overflowX="auto">
            <Heading size="sm" mb={3} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
              Breakdown
            </Heading>
            <Box
              as="table"
              w="100%"
              bg={colorMode === 'light' ? 'white' : 'gray.700'}
              rounded="lg"
              boxShadow="sm"
              overflow="hidden"
              fontSize="sm"
            >
              <Box as="thead" bg={colorMode === 'light' ? 'gray.100' : 'gray.600'}>
                <Box as="tr">
                  {['Tag', 'Trades', 'Wins', 'Losses', 'Win Rate', 'Total P&L', 'Avg P&L'].map((h) => (
                    <Box
                      as="th"
                      key={h}
                      p={3}
                      textAlign="left"
                      fontWeight="semibold"
                      color={colorMode === 'light' ? 'gray.600' : 'gray.200'}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box as="tbody">
                {tagPerformance.map((t, i) => (
                  <Box
                    as="tr"
                    key={t.tag_id}
                    bg={i % 2 === 0 ? 'transparent' : colorMode === 'light' ? 'gray.50' : 'gray.750'}
                    cursor="pointer"
                    _hover={{ bg: colorMode === 'light' ? 'blue.50' : 'gray.600' }}
                    onClick={() => handleTagClick(t.tag_id)}
                  >
                    <Box as="td" p={3} fontWeight="bold">
                      <Badge bg={t.color || '#3182CE'} color="white" fontSize="xs" px={2} py={0.5} rounded="md">
                        {t.name}
                      </Badge>
                    </Box>
                    <Box as="td" p={3}>{t.num_trades}</Box>
                    <Box as="td" p={3} color="green.400">{t.num_wins}</Box>
                    <Box as="td" p={3} color="red.400">{t.num_losses}</Box>
                    <Box as="td" p={3}>
                      {t.win_rate !== null ? (
                        <Badge colorScheme={t.win_rate >= 50 ? 'green' : 'red'}>
                          {t.win_rate}%
                        </Badge>
                      ) : '—'}
                    </Box>
                    <Box as="td" p={3} color={t.total_pnl >= 0 ? 'green.400' : 'red.400'} fontWeight="semibold">
                      {t.total_pnl >= 0 ? '+' : ''}{currencySymbol}{t.total_pnl.toFixed(2)}
                    </Box>
                    <Box as="td" p={3} color={t.avg_pnl !== null && t.avg_pnl >= 0 ? 'green.400' : 'red.400'}>
                      {t.avg_pnl !== null ? `${t.avg_pnl >= 0 ? '+' : ''}${currencySymbol}${t.avg_pnl.toFixed(2)}` : '—'}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* TRADE-958: Tag Combinations */}
          <Box overflowX="auto">
            <Heading size="sm" mb={3} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
              Tag Combinations
            </Heading>
            {tagCoOccurrence.length === 0 ? (
              <Text fontSize="sm" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                No multi-tag trades yet — apply 2+ tags to a trade to see combinations.
              </Text>
            ) : (
              <Box
                as="table"
                w="100%"
                bg={colorMode === 'light' ? 'white' : 'gray.700'}
                rounded="lg"
                boxShadow="sm"
                overflow="hidden"
                fontSize="sm"
              >
                <Box as="thead" bg={colorMode === 'light' ? 'gray.100' : 'gray.600'}>
                  <Box as="tr">
                    {['Combination', 'Trades', 'Wins', 'Losses', 'Win Rate', 'Total P&L', 'Avg P&L'].map((h) => (
                      <Box
                        as="th"
                        key={h}
                        p={3}
                        textAlign="left"
                        fontWeight="semibold"
                        color={colorMode === 'light' ? 'gray.600' : 'gray.200'}
                      >
                        {h}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box as="tbody">
                  {tagCoOccurrence.map((c, i) => (
                    <Box
                      as="tr"
                      key={c.combo.join('+')}
                      bg={i % 2 === 0 ? 'transparent' : colorMode === 'light' ? 'gray.50' : 'gray.750'}
                      _hover={{ bg: colorMode === 'light' ? 'blue.50' : 'gray.600' }}
                    >
                      <Box as="td" p={3}>
                        <HStack spacing={1} flexWrap="wrap">
                          {c.combo.map((name) => {
                            const tag = tagPerformance.find((t) => t.name === name);
                            return (
                              <Badge
                                key={name}
                                bg={tag?.color || '#3182CE'}
                                color="white"
                                fontSize="xs"
                                px={2}
                                py={0.5}
                                rounded="md"
                              >
                                {name}
                              </Badge>
                            );
                          })}
                        </HStack>
                      </Box>
                      <Box as="td" p={3}>{c.num_trades}</Box>
                      <Box as="td" p={3} color="green.400">{c.num_wins}</Box>
                      <Box as="td" p={3} color="red.400">{c.num_losses}</Box>
                      <Box as="td" p={3}>
                        {c.win_pct !== null ? (
                          <Badge colorScheme={c.win_pct >= 50 ? 'green' : 'red'}>
                            {c.win_pct}%
                          </Badge>
                        ) : '—'}
                      </Box>
                      <Box
                        as="td"
                        p={3}
                        color={c.total_pnl >= 0 ? 'green.400' : 'red.400'}
                        fontWeight="semibold"
                      >
                        {c.total_pnl >= 0 ? '+' : ''}{currencySymbol}{c.total_pnl.toFixed(2)}
                      </Box>
                      <Box
                        as="td"
                        p={3}
                        color={c.avg_pnl !== null && c.avg_pnl >= 0 ? 'green.400' : 'red.400'}
                      >
                        {c.avg_pnl !== null
                          ? `${c.avg_pnl >= 0 ? '+' : ''}${currencySymbol}${c.avg_pnl.toFixed(2)}`
                          : '—'}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* TRADE-958: Per-Tag Timeline */}
          <Box>
            <Flex align="center" justify="space-between" mb={3} flexWrap="wrap" gap={2}>
              <Heading size="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
                Tag Timeline
              </Heading>
              <FilterSelect
                value={selectedTagId}
                onChange={(e) => setSelectedTagId(e.target.value)}
                placeholder="Select tag…"
                variant="filled"
                size="sm"
                maxW="200px"
                bg={colorMode === 'light' ? 'white' : 'gray.700'}
                _hover={{ bg: colorMode === 'light' ? 'gray.50' : 'gray.600' }}
                rounded="md"
                options={tagPerformance.map((t) => ({ label: t.name, value: String(t.tag_id) }))}
              />
            </Flex>
            {selectedTagId && tagTimeseries.series && tagTimeseries.series.length > 0 ? (
              <Box bg={colorMode === 'light' ? 'white' : 'gray.700'} rounded="lg" p={4} h="320px">
                {/*
                  Sign-colored P&L bars per period. Trade count is preserved
                  in a custom tooltip so the chart stays single-axis and the
                  P&L trend reads at a glance. Tooltip uses HTML so we can
                  pull in the trade count alongside the formatted P&L.
                */}
                <Chart
                  chartType="ColumnChart"
                  width="100%"
                  height="100%"
                  data={[
                    [
                      'Period',
                      `P&L (${currencySymbol})`,
                      { role: 'style' },
                      { role: 'tooltip', p: { html: true } },
                    ],
                    ...tagTimeseries.series.map((s) => {
                      const pnl = Number(s.total_pnl || 0);
                      const color = pnl >= 0 ? '#48BB78' : '#FC8181';
                      const sign = pnl >= 0 ? '+' : '';
                      const tooltipBg = colorMode === 'light' ? '#FFFFFF' : '#2D3748';
                      const tooltipText = colorMode === 'light' ? '#1A202C' : '#F7FAFC';
                      const tooltipMuted = colorMode === 'light' ? '#718096' : '#A0AEC0';
                      const tooltip =
                        `<div style="padding:8px 12px;background:${tooltipBg};color:${tooltipText};font-family:-apple-system,sans-serif;font-size:12px;min-width:140px;">` +
                          `<div style="font-weight:600;margin-bottom:4px;">${formatPeriodLabel(s.period)}</div>` +
                          `<div style="color:${color};font-weight:600;">${sign}${currencySymbol}${pnl.toFixed(2)}</div>` +
                          `<div style="color:${tooltipMuted};">${s.num_trades} trade${s.num_trades === 1 ? '' : 's'}` +
                            (s.win_pct != null ? ` · ${s.win_pct}% WR` : '') +
                          `</div>` +
                        `</div>`;
                      return [formatPeriodLabel(s.period), pnl, color, tooltip];
                    }),
                  ]}
                  options={{
                    backgroundColor: colorMode === 'light' ? '#FDFDFD' : '#1A202C',
                    chartArea: { left: 70, right: 20, top: 20, bottom: 60 },
                    legend: 'none',
                    tooltip: { isHtml: true, trigger: 'focus' },
                    hAxis: {
                      textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
                      slantedText: true,
                      slantedTextAngle: 30,
                    },
                    vAxis: {
                      title: `P&L (${currencySymbol})`,
                      titleTextStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0', italic: false },
                      textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
                      baselineColor: colorMode === 'light' ? '#CBD5E0' : '#4A5568',
                    },
                  }}
                />
              </Box>
            ) : selectedTagId ? (
              <Text fontSize="sm" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                No data for this tag yet.
              </Text>
            ) : null}
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
}
